import React, { useState, useEffect } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { Button, Input } from './ui';

const CreateRoom: React.FC = () => {
  const [roomName, setRoomName] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Check if user has an active room session
  useEffect(() => {
    const currentRoomId = localStorage.getItem('currentRoomId');
    const participantId = localStorage.getItem('participantId');

    // If user has an active room session, redirect them back
    if (currentRoomId && participantId) {
      navigate(`/room/${currentRoomId}`);
    }
  }, [navigate]);

  const createRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roomName.trim()) return;

    setLoading(true);
    try {
      // Clear any existing session data
      localStorage.removeItem('currentRoomId');
      localStorage.removeItem('planningPokerProfile');
      localStorage.removeItem('participantId');
      localStorage.removeItem('currentRoomName');
      localStorage.removeItem('isRoomCreator');
      localStorage.removeItem('needsProfileForNewRoom');

      const roomData = {
        name: roomName.trim(),
        adminId: 'pending',
        isActive: true,
        createdAt: new Date()
      };

      const docRef = await addDoc(collection(db, 'rooms'), roomData);

      localStorage.setItem('currentRoomId', docRef.id);
      localStorage.setItem('currentRoomName', roomName.trim());
      localStorage.setItem('isRoomCreator', 'true');
      localStorage.setItem('needsProfileForNewRoom', 'true');

      navigate(`/room/${docRef.id}`);
    } catch (error) {
      // Error creating room
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20 backdrop-blur-3xl"></div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%2523ffffff%22%20fill-opacity%3D%220.03%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>

      {/* Navbar */}
      <nav className="relative z-10 flex items-center justify-between px-8 py-3 ">
        <div className="flex items-center gap-3">
          {/* Logo */}
          <div className="relative">
            <div className="w-8 h-10 bg-white/20 rounded border border-white/30 flex items-center justify-center">
              <div className="relative">
                <div className="w-6 h-8 bg-blue-300 rounded-[2px] flex items-center justify-center">
                  <span className="text-white font-semibold text-xs">5</span>
                  <div className="absolute top-0.5 left-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                  <div className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
            <div className="absolute -right-1 -bottom-1 w-8 h-10 bg-white/20 rounded border border-white/30 flex items-center justify-center -rotate-6">
              <div className="relative">
                <div className="w-6 h-8 bg-gray-800 rounded-[2px] flex items-center justify-center">
                  <span className="text-white font-semibold text-2xl">1</span>
                  <div className="absolute top-0.5 left-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                  <div className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
          <span className="text-xl font-medium text-white ml-8">Scrumo</span>
        </div>

        {/* Nav Links */}
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/')} className="px-4 py-2 text-sm text-white hover:bg-white/20 rounded-full border border-white/30 transition-colors">
            Home
          </button>
          <button onClick={() => navigate('/blog')} className="px-4 py-2 text-sm text-white hover:bg-white/20 rounded-full transition-colors">
            Blog
          </button>
          <button onClick={() => navigate('/faq')} className="px-4 py-2 text-sm text-white hover:bg-white/20 rounded-full transition-colors">
            FAQ
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-65px)] px-4">
        {/* People planning badge */}
        <div className="mb-4 px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full flex items-center gap-3">
          <div className="flex -space-x-2">
            <img src="https://github.com/shadcn.png" alt="User 1" className="w-5 h-5 rounded-full border-2 border-white" />
            <img src="https://github.com/leerob.png" alt="User 2" className="w-5 h-5 rounded-full border-2 border-white" />
            <img src="https://github.com/evilrabbit.png" alt="User 3" className="w-5 h-5 rounded-full border-2 border-white" />
          </div>
          <span className="text-xs text-purple-200">People are planning their next story.</span>
        </div>

        {/* Floating card decoration */}
        <div className="absolute top-48 left-64 rotate-12">
          <div className="w-11 h-14 bg-white/10 backdrop-blur-xl rounded border-2 border-white/20 flex items-center justify-center shadow-lg">
            <div className="relative">
              <div className="w-8 h-10 bg-gray-800 rounded-[2px] flex items-center justify-center">
                <span className="text-white font-semibold text-lg">8</span>
                <div className="absolute top-0.5 left-0.5 w-1 h-1 bg-white rounded-full"></div>
                <div className="absolute bottom-0.5 right-0.5 w-1 h-1 bg-white rounded-full"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Text */}
        <div className="text-center mb-8">
          <h1 className="text-4xl text-white mb-2">
            Now Empower Your <span className="font-semibold">Agile Journey!</span>
          </h1>
          <p className="text-lg text-purple-200">Easy-to-use and fun story point estimations.</p>
        </div>

        {/* Form Card */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-2xl max-w-sm w-full">
          {/* Rocket Icon */}
          <div className="w-11 h-11 bg-white/10 backdrop-blur-xl rounded-full flex items-center justify-center mx-auto mb-6 border border-white/20">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.5 16.5C3.5 17.76 3.5 21 3.5 21S6.74 21 8 20C9.26 19 10 17.5 10 17.5" stroke="#FA9815" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 15L8 19" stroke="#27272A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10.5 12.5L6 17L7 21.5L11.5 22.5L16 18L13.5 14.5" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7.5 10L7 10.5" stroke="#F9F9F9" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M13 2S20 2 22 4C24 6 24 13 24 13L13 24L0 11L11 0L13 2Z" stroke="#27272A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="15.5" cy="8.5" r="1.5" fill="#F9F9F9" stroke="#27272A" strokeWidth="1.5"/>
            </svg>
          </div>

          <p className="text-sm font-semibold text-center text-white mb-6">
            Start a new estimation session.
          </p>

          <div className="space-y-4">
            <form onSubmit={createRoom} className="bg-white/10 backdrop-blur-xl rounded-xl p-6 space-y-5 border border-white/20">
              <div>
                <label htmlFor="roomName" className="block text-sm font-semibold text-purple-200 mb-1">
                  Room Name
                </label>
                <Input
                  id="roomName"
                  type="text"
                  placeholder="Room name"
                  value={roomName}
                  onChange={(e) => setRoomName(e.target.value)}
                  required
                  className="w-full bg-white/10 border-white/30 text-white placeholder-blue-200/50"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full border !text-sm border-white/30 bg-white/10 !text-white hover:bg-white/20 backdrop-blur-xl transition-colors"
              >
                {loading ? 'Creating...' : 'Create Room'}
              </Button>
            </form>

            <div className="text-center">
              <span className="text-sm font-semibold text-purple-200">Or</span>
            </div>

            <Button
              type="button"
              onClick={() => navigate('/join')}
              className="w-full text-white !text-sm bg-gradient-to-br from-purple-500 to-blue-500 transition-colors flex items-center justify-center gap-2"
            >
              Join Room
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="currentColor" fillOpacity="0.2"/>
                <path d="M12 16L16 12L12 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Button>
          </div>
        </div>

        {/* Cursor decoration */}
        <div className="absolute bottom-32 right-96 pointer-events-none">
          <svg width="24" height="28" viewBox="0 0 24 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 0L0 20.5L5.5 15L10 27L14 25L9.5 13L17.5 13L0 0Z" fill="#27272A"/>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default CreateRoom;
